import { z } from 'zod';

// ========================================
// BUSINESS JOURNEY E2E TYPES (Issue #168)
// ========================================

/**
 * Business Journey Scenario Codes (Scenarios 16-25)
 * These are higher-level tests that validate complete user workflows
 * abstracted by realm template (LOS, Blank)
 * V1: LOS only (Issue #68)
 */
export const BusinessJourneyScenarioCodeSchema = z.enum([
  // Scenario 16: Asset/Portfolio Overview
  'asset_overview',
  // Scenario 17: Predictive Maintenance
  'predictive_maintenance',
  // Scenario 18: Stakeholder Analysis
  'stakeholder_analysis',
  // Scenario 19: Investigation (Root Cause)
  'investigation',
  // Scenario 20: Optimization
  'optimization',
  // Scenario 21: Safety Workflow (LOS compliance - V1: Issue #68)
  'safety_workflow',
  // Scenario 22: Compliance Audit
  'compliance_audit',
  // Scenario 23: Report Generation
  'report_generation',
  // Scenario 24: Multi-Service Workflow
  'multi_service_workflow',
  // Scenario 25: Blank Realm Operations
  'blank_realm_operations',
]);

export type BusinessJourneyScenarioCode = z.infer<typeof BusinessJourneyScenarioCodeSchema>;

/**
 * Abstract entity mapping from generic names to realm-specific concepts
 * Maps stored in RealmTemplate.entityMappings in Platform DB
 */
export const EntityMappingSchema = z.object({
  // Core asset types
  primaryAsset: z.string().optional(), // loan (LOS), project (Blank)
  equipment: z.string().optional(), // property (LOS), equipment (Blank)

  // Metrics and predictions
  healthMetric: z.string().optional(), // loanHealthScore (LOS), health_score (Blank)
  failurePrediction: z.string().optional(), // SLA breach probability

  // Stakeholders and events
  stakeholder: z.string().optional(), // borrower (LOS), user (Blank)
  financialEvent: z.string().optional(), // draw_disbursement (LOS)
  safetyEvent: z.string().optional(), // fraud_alert (LOS)

  // Optimization and investigation
  optimizationTarget: z.string().optional(), // pipeline_velocity (LOS)
  investigationCase: z.string().optional(), // exception_investigation (LOS)

  // LOS-specific mappings (Issue #253)
  underwriting: z.string().optional(), // Underwriting entity type
  draw: z.string().optional(), // Draw/disbursement entity type
  collateral: z.string().optional(), // Collateral entity type
  condition: z.string().optional(), // Loan condition entity type
  loanOfficer: z.string().optional(), // Loan officer entity type
  appraisal: z.string().optional(), // Appraisal entity type
});

export type EntityMapping = z.infer<typeof EntityMappingSchema>;

/**
 * Fixture mapping for realm-specific test data
 */
export const FixtureMappingSchema = z.object({
  digitalTwin: z.string().optional(), // Path to Digital Twin fixtures
  featureStore: z.string().optional(), // Path to Feature Store fixtures
  compliance: z.string().optional(), // Path to Compliance rules
  workflows: z.string().optional(), // Path to Workflow templates
  reports: z.string().optional(), // Path to Report templates
});

export type FixtureMapping = z.infer<typeof FixtureMappingSchema>;

/**
 * Realm configuration for business journey tests
 * Loaded from Platform DB RealmTemplate
 */
export const RealmConfigSchema = z.object({
  // Realm identification
  realmId: z.string(), // Actual realm being tested
  templateCode: z.string(), // 'los' | 'blank' | custom
  templateName: z.string(),

  // Entity mappings (null for blank templates)
  entityMapping: EntityMappingSchema.nullable(),

  // Fixture file locations
  fixtures: FixtureMappingSchema.nullable(),

  // Whether this is a system template
  isSystemTemplate: z.boolean(),
});

export type RealmConfig = z.infer<typeof RealmConfigSchema>;

/**
 * Journey step types
 */
export const JourneyStepTypeSchema = z.enum([
  'load_data', // Load data from a service
  'validate', // Validate data/state
  'execute', // Execute an action
  'wait', // Wait for condition
  'assert', // Assert expected outcome
  'cleanup', // Cleanup resources
]);

export type JourneyStepType = z.infer<typeof JourneyStepTypeSchema>;

/**
 * Journey step status
 */
export const JourneyStepStatusSchema = z.enum(['pending', 'running', 'passed', 'failed', 'skipped']);

export type JourneyStepStatus = z.infer<typeof JourneyStepStatusSchema>;

/**
 * Single step in a business journey
 */
export const JourneyStepSchema = z.object({
  // Step identification
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),

  // Step type and target
  type: JourneyStepTypeSchema,
  service: z.string(), // Target service (e.g., '18-digital-twin-service')
  operation: z.string(), // Operation to perform

  // Input configuration (supports template variables)
  input: z.record(z.string(), z.unknown()).optional(),

  // Dependencies and execution
  dependsOn: z.array(z.string()).default([]),
  timeoutMs: z.number().default(30000),
  retryOnFailure: z.boolean().default(false),
  maxRetries: z.number().default(3),
  continueOnError: z.boolean().default(false),

  // Realm-specific configuration (V1: LOS only - Issue #68)
  realmSpecific: z
    .object({
      los: z.record(z.string(), z.unknown()).optional(),
      blank: z.record(z.string(), z.unknown()).optional(),
    })
    .optional(),

  // Skip conditions (e.g., skip blank_realm_operations for LOS)
  skipForTemplates: z.array(z.string()).optional(),
});

export type JourneyStep = z.infer<typeof JourneyStepSchema>;

/**
 * Journey step execution result
 */
export const JourneyStepResultSchema = z.object({
  stepId: z.string(),
  status: JourneyStepStatusSchema,
  durationMs: z.number(),
  output: z.record(z.string(), z.unknown()).optional(),
  error: z.string().optional(),
  retryCount: z.number().default(0),
  startedAt: z.date(),
  completedAt: z.date().optional(),
});

export type JourneyStepResult = z.infer<typeof JourneyStepResultSchema>;

/**
 * Journey assertion types
 */
export const JourneyAssertionTypeSchema = z.enum([
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'greater_than',
  'less_than',
  'matches_regex',
  'is_defined',
  'is_null',
  'array_length',
  'multi_tenant_isolation', // Special assertion for security
]);

export type JourneyAssertionType = z.infer<typeof JourneyAssertionTypeSchema>;

/**
 * Journey assertion definition
 */
export const JourneyAssertionSchema = z.object({
  // Assertion identification
  id: z.string(),
  description: z.string(),

  // Assertion type and target
  type: JourneyAssertionTypeSchema,
  path: z.string(), // JSON path to value in step results
  stepId: z.string().optional(), // Which step's output to check

  // Expected value
  expected: z.unknown().optional(),

  // Severity if assertion fails
  severity: z.enum(['critical', 'warning', 'info']).default('critical'),

  // Realm-specific expected values (V1: LOS only - Issue #68)
  realmSpecific: z
    .object({
      los: z.unknown().optional(),
      blank: z.unknown().optional(),
    })
    .optional(),
});

export type JourneyAssertion = z.infer<typeof JourneyAssertionSchema>;

/**
 * Journey execution status
 */
export const JourneyExecutionStatusSchema = z.enum(['pending', 'running', 'passed', 'failed', 'timeout', 'cancelled']);

export type JourneyExecutionStatus = z.infer<typeof JourneyExecutionStatusSchema>;

/**
 * Business journey scenario definition
 */
export const BusinessJourneyScenarioSchema = z.object({
  // Scenario identification
  id: z.string(),
  scenarioCode: BusinessJourneyScenarioCodeSchema,
  name: z.string(),
  description: z.string().optional(),

  // Scenario version
  version: z.string().default('1.0.0'),

  // Supported realm templates
  supportedTemplates: z.array(z.string()), // ['los', 'blank']

  // Configuration
  businessId: z.string(),
  realmConfig: RealmConfigSchema.optional(),

  // Journey steps
  steps: z.array(JourneyStepSchema),

  // Assertions
  assertions: z.array(JourneyAssertionSchema),

  // Execution constraints
  maxDurationMs: z.number().default(300000), // 5 minutes default
  priority: z.number().default(2), // 1 = highest

  // Tags and metadata
  tags: z.array(z.string()),
  enabled: z.boolean().default(true),
});

export type BusinessJourneyScenario = z.infer<typeof BusinessJourneyScenarioSchema>;

/**
 * Business journey execution result
 */
export const BusinessJourneyExecutionResultSchema = z.object({
  // Execution identification
  id: z.string(),
  scenario: BusinessJourneyScenarioSchema,

  // Context
  businessId: z.string(),
  realmId: z.string().optional(),
  templateCode: z.string(),

  // Status and timing
  status: JourneyExecutionStatusSchema,
  startedAt: z.date(),
  completedAt: z.date().optional(),
  durationMs: z.number().optional(),

  // Step results
  stepResults: z.array(JourneyStepResultSchema),

  // Assertion results
  assertionResults: z.array(
    z.object({
      assertionId: z.string(),
      passed: z.boolean(),
      actualValue: z.unknown().optional(),
      expectedValue: z.unknown().optional(),
      error: z.string().optional(),
    }),
  ),

  // Summary
  passedSteps: z.number(),
  failedSteps: z.number(),
  skippedSteps: z.number(),
  passedAssertions: z.number(),
  failedAssertions: z.number(),

  // Error details
  failureReason: z.string().optional(),
  traceId: z.string().optional(),
});

export type BusinessJourneyExecutionResult = z.infer<typeof BusinessJourneyExecutionResultSchema>;

/**
 * Demo data reference for fixtures
 */
export const DemoDataReferenceSchema = z.object({
  // Equipment with demo flags
  equipmentId: z.string(),
  demoFlag: z.string(), // '_demo' suffix indicates demo scenario data

  // Associated metrics
  healthScore: z.number().optional(),
  failureProbability: z.number().optional(),
  daysToFailure: z.number().optional(),
  hoursToFailure: z.number().optional(),

  // Scenario-specific data
  scenarioData: z.record(z.string(), z.unknown()).optional(),
});

export type DemoDataReference = z.infer<typeof DemoDataReferenceSchema>;

/**
 * Business journey test configuration
 */
export const BusinessJourneyTestConfigSchema = z.object({
  // Which scenarios to run
  scenarioCodes: z.array(BusinessJourneyScenarioCodeSchema).optional(),

  // Which templates to test
  templateCodes: z.array(z.string()).optional(),

  // Execution options
  parallelExecution: z.boolean().default(false),
  stopOnFirstFailure: z.boolean().default(false),
  cleanupAfterTest: z.boolean().default(true),

  // Realm options
  useExistingRealm: z.boolean().default(false),
  realmId: z.string().optional(),
  ttlMinutes: z.number().default(30),

  // Fixture options
  seedDemoData: z.boolean().default(true),
  useDemoFlags: z.boolean().default(true),
});

export type BusinessJourneyTestConfig = z.infer<typeof BusinessJourneyTestConfigSchema>;

/**
 * Business journey test summary
 */
export const BusinessJourneyTestSummarySchema = z.object({
  // Test run identification
  testRunId: z.string(),
  startedAt: z.date(),
  completedAt: z.date(),
  durationMs: z.number(),

  // Configuration used
  config: BusinessJourneyTestConfigSchema,

  // Results per template
  templateResults: z.record(
    z.string(),
    z.object({
      templateCode: z.string(),
      templateName: z.string(),
      scenariosPassed: z.number(),
      scenariosFailed: z.number(),
      scenariosSkipped: z.number(),
      executionResults: z.array(BusinessJourneyExecutionResultSchema),
    }),
  ),

  // Overall summary
  totalScenarios: z.number(),
  totalPassed: z.number(),
  totalFailed: z.number(),
  totalSkipped: z.number(),
  successRate: z.number(), // 0-100

  // Critical failures
  criticalFailures: z.array(
    z.object({
      templateCode: z.string(),
      scenarioCode: z.string(),
      failureReason: z.string(),
      assertionId: z.string().optional(),
    }),
  ),
});

export type BusinessJourneyTestSummary = z.infer<typeof BusinessJourneyTestSummarySchema>;
